import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import Loader from "../../Components/Loader/Loader";
import Notification from "../../Components/Notification/Notification";
import ConfirmDialog from "../../Components/ConfirmDialog/ConfirmDialog";
import remove from "../../img/remove_circle_outline-24px.svg";
import API from "../../API/REST_API";
import { CURRENCIES } from "../../constants/currencies";

// Floating Label Input Component
const FloatingInput = ({
  id,
  label,
  type = "text",
  value,
  onChange,
  disabled = false,
  error = false,
  errorMessage = "",
}) => (
  <div className="relative">
    <input
      type={type}
      id={id}
      className={`peer w-full rounded-md border bg-white px-3 pb-2 pt-5 text-base md:text-sm placeholder-transparent transition-colors focus:outline-none ${
        disabled
          ? "border-slate-200 bg-slate-100 text-slate-500"
          : error
          ? "border-red-500 focus:border-red-500"
          : "border-slate-300 focus:border-slate-500"
      }`}
      placeholder={label}
      value={value || ""}
      onChange={onChange}
      disabled={disabled}
    />
    <label
      htmlFor={id}
      className={`pointer-events-none absolute left-3 top-1 text-xs transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs ${
        disabled
          ? "text-slate-400"
          : error
          ? "text-red-500 peer-focus:text-red-500"
          : "text-slate-500 peer-focus:text-slate-700"
      }`}
    >
      {label}
    </label>
    {error && errorMessage && (
      <p className="mt-1 text-xs text-red-500">{errorMessage}</p>
    )}
  </div>
);

// Phone formatting utilities
const formatPhoneNumber = (value) => {
  if (!value) return "";
  // Remove all non-digit characters
  const phoneNumber = value.replace(/\D/g, "");
  const phoneNumberLength = phoneNumber.length;
  
  // Format based on length
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const getDigitsOnly = (value) => {
  if (!value) return "";
  return value.replace(/\D/g, "");
};

const isValidPhoneNumber = (value) => {
  if (!value) return true; // Empty is valid (not required)
  const digits = getDigitsOnly(value);
  return digits.length >= 10 && digits.length <= 15;
};

// Floating Label Phone Input Component
const FloatingPhoneInput = ({
  id,
  label,
  value,
  onChange,
  error = false,
  errorMessage = "",
}) => {
  const handleChange = (e) => {
    const inputValue = e.target.value;
    const digits = getDigitsOnly(inputValue);
    // Limit to 15 digits (international standard max)
    if (digits.length <= 15) {
      onChange(formatPhoneNumber(digits));
    }
  };

  return (
    <div className="relative">
      <input
        type="tel"
        id={id}
        className={`peer w-full rounded-md border bg-white px-3 pb-2 pt-5 text-base md:text-sm placeholder-transparent transition-colors focus:outline-none ${
          error
            ? "border-red-500 focus:border-red-500"
            : "border-slate-300 focus:border-slate-500"
        }`}
        placeholder={label}
        value={value || ""}
        onChange={handleChange}
        inputMode="tel"
        autoComplete="tel"
      />
      <label
        htmlFor={id}
        className={`pointer-events-none absolute left-3 top-1 text-xs transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs ${
          error
            ? "text-red-500 peer-focus:text-red-500"
            : "text-slate-500 peer-focus:text-slate-700"
        }`}
      >
        {label}
      </label>
      {error && errorMessage && (
        <p className="mt-1 text-xs text-red-500">{errorMessage}</p>
      )}
    </div>
  );
};

// Floating Label Select Component
const FloatingSelect = ({ id, label, value, onChange, options, onBlur }) => (
  <div className="relative">
    <select
      id={id}
      className="peer w-full appearance-none rounded-md border border-slate-300 bg-white px-3 pb-2 pt-5 text-base md:text-sm focus:border-slate-500 focus:outline-none"
      value={value || ""}
      onChange={onChange}
      onBlur={onBlur}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <label
      htmlFor={id}
      className="pointer-events-none absolute left-3 top-1 text-xs text-slate-500"
    >
      {label}
    </label>
    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
      <svg
        className="h-4 w-4 text-slate-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </div>
  </div>
);

// Accordion Section Component
const AccordionSection = ({
  title,
  description,
  icon,
  isOpen,
  onToggle,
  children,
}) => (
  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between px-4 py-4 text-left transition-colors hover:bg-slate-50"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
          {icon}
        </div>
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            {title}
          </h3>
          <p className="text-sm text-slate-500">
            {description}
          </p>
        </div>
      </div>
      <svg
        className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${
          isOpen ? "rotate-180" : ""
        }`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>
    <div
      className={`transition-all duration-300 ease-in-out ${
        isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
      }`}
    >
      <div className="border-t border-slate-200 px-4 py-4">
        {children}
      </div>
    </div>
  </div>
);

// Icons
const UserIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const FolderIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
    />
  </svg>
);

const CogIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

class Settings extends Component {
  state = {
    loading: true,
    countries: [],
    personal_details_error: "",
    user: this.props.user,
    notification: {
      show: false,
      message: "",
      type: "success",
    },
    personal_update: false,
    categories: [], // Full category objects with budget/spent info
    editingCategoryId: null, // ID of category being edited
    editingField: null, // Field being edited: 'title' or 'budget'
    new_category: "",
    new_category_budget: "",
    new_category_error: false,
    new_category_error_msg: "",
    add_category: false,
    add_category_error: "",
    delete_category: false,
    showDeleteConfirm: false,
    categoryToDelete: null,
    categoryToDeleteName: "",
    showDeleteAccountConfirm: false,
    deletingAccount: false,
    settings: {
      currency: "",
      currencies: CURRENCIES.map((c) => ({ label: c.label, value: c.code })),
    },
    // Accordion state - all drawers minimized by default
    openSections: {
      personal: false,
      categories: false,
      general: false,
    },
    // Validation state
    mobileError: false,
    mobileErrorMsg: "",
  };

  toggleSection = (section) => {
    this.setState((prev) => ({
      openSections: {
        ...prev.openSections,
        [section]: !prev.openSections[section],
      },
    }));
  };

  showNotification(message, type = "success") {
    this.setState({
      notification: { show: true, message, type },
    });
  }

  hideNotification = () => {
    this.setState({
      notification: { show: false, message: "", type: "success" },
    });
  };

  async updateUserHandlers() {
    // Validate mobile before submitting
    if (this.state.user.mobile && !isValidPhoneNumber(this.state.user.mobile)) {
      this.setState({
        mobileError: true,
        mobileErrorMsg: "Please enter a valid phone number (at least 10 digits)",
      });
      this.showNotification("Please fix the mobile number before saving", "warning");
      return;
    }

    const updateURL = this.props.baseUrl + this.props.update_profile_url;

    // Send all fields including empty strings (backend will handle clearing)
    const body = {
      first_name: this.state.user.first_name,
      last_name: this.state.user.last_name,
      mobile: getDigitsOnly(this.state.user.mobile),
    };
    this.setState({ personal_update: true });

    try {
      const { response, data } = await API.Patch(updateURL, body);

      if (response && response.status === 200) {
        localStorage.setItem("first_name", data.first_name);
        localStorage.setItem("last_name", data.last_name);
        this.setState({ personal_update: false });
        this.showNotification("Profile updated successfully!", "success");
      }

      if (response && response.status === 400) {
        throw new Error("something went wrong, please try again later");
      }
    } catch (error) {
      this.setState({
        personal_details_error: "*" + error,
        personal_update: false,
      });
    }
  }

  async getAllCategories() {
    const updateURL = this.props.baseUrl + this.props.apiRouts.CATEGORIES;
    try {
      const result = await API.Get(updateURL);
      if (result) {
        // Store full category objects with budget and spending info
        this.setState({ categories: result });
      }
    } catch (error) {
      // Handle error silently
    }
  }

  async getSettnigs() {
    const updateURL = this.props.baseUrl + this.props.apiRouts.SETTINGS;
    try {
      const result = await API.Get(updateURL);
      if (result) {
        let settings = JSON.parse(JSON.stringify(this.state.settings));
        // Normalize legacy "CODE-SYMBOL" format to code only
        settings.currency = result.currency?.includes("-")
          ? result.currency.split("-")[0]
          : result.currency;
        this.setState({ settings });
      }
    } catch (error) {
      // Handle error silently
    }
  }

  async updateSettnigs(obj) {
    const updateURL = this.props.baseUrl + this.props.apiRouts.SETTINGS;
    try {
      let body = { currency: obj.currency };
      const { response } = await API.Patch(updateURL, body);

      if (response && response.status === 200) {
        this.showNotification("Currency updated!", "success");
        // Notify parent to refresh settings
        if (this.props.onSettingsUpdate) {
          this.props.onSettingsUpdate();
        }
      }
    } catch (error) {
      console.log("save currency ", error);
    }
  }

  async loadUserProfile() {
    const getProfileURL = this.props.baseUrl + this.props.get_profile;
    try {
      const result = await API.Get(getProfileURL);
      if (result) {
        // Format the mobile number if it exists
        if (result.mobile) {
          result.mobile = formatPhoneNumber(result.mobile);
        }
        this.setState({ user: result, loading: false });
      }
    } catch (error) {
      // Handle error silently
    }
  }

  async componentDidMount() {
    await this.getAllCategories();
    await this.getSettnigs();
    await this.loadUserProfile();
    
    // Expand Categories section when navigating from Budget (e.g. category help link)
    const params = new URLSearchParams(this.props.location?.search || "");
    if (params.get("expand") === "categories") {
      this.setState((prev) => ({
        openSections: { ...prev.openSections, categories: true },
      }));
    }
  }

  onChange(type, value) {
    let user = JSON.parse(JSON.stringify(this.state.user));
    if (type === "lastName") {
      user.last_name = value;
      this.setState({ user });
      return;
    }
    if (type === "firstName") {
      user.first_name = value;
      this.setState({ user });
      return;
    }
    if (type === "mobile") {
      user.mobile = value;
      // Validate mobile number
      const isValid = isValidPhoneNumber(value);
      this.setState({
        user,
        mobileError: value && !isValid,
        mobileErrorMsg: value && !isValid ? "Please enter a valid phone number (at least 10 digits)" : "",
      });
      return;
    }
    if (type === "new_category") {
      this.setState({ new_category: value });
      return;
    }
    if (type === "new_category_budget") {
      this.setState({ new_category_budget: value });
      return;
    }
    if (type === "currency") {
      let settings = JSON.parse(JSON.stringify(this.state.settings));
      settings.currency = value;
      this.setState({ settings });
    }
  }

  setStateAsync(state) {
    return new Promise((resolve) => {
      this.setState(state, resolve);
    });
  }

  async addNewCategoryHandler() {
    if (this.state.new_category === "") {
      this.showNotification("New category can't be empty", "warning");
      return;
    }

    if (this.checkCategoryExist()) return;

    const updateURL = this.props.baseUrl + this.props.apiRouts.CATEGORIES;
    const budgetValue = this.state.new_category_budget ? parseFloat(this.state.new_category_budget) : null;
    const body = { 
      title: this.state.new_category,
      budget: budgetValue
    };
    this.setState({ add_category: true });

    try {
      const { response } = await API.Post(updateURL, body);

      if (response && response.status === 201) {
        await this.getAllCategories(); // Refresh categories list
        this.setState({
          add_category: false,
          new_category: "",
          new_category_budget: "",
        });
        this.showNotification("New category added!", "success");
      }

      if (response && response.status === 400) {
        throw new Error("something went wrong, please try again later");
      }
    } catch (error) {
      this.setState({
        add_category_error: "*" + error,
        add_category: false,
      });
    }
  }

  deleteCategoryHandler = (categoryId) => {
    // Find the category name for the confirmation message
    const category = this.state.categories.find(cat => (cat.id || cat.value) === categoryId);
    const categoryName = category ? (category.title || category.label) : "this category";
    
    this.setState({
      showDeleteConfirm: true,
      categoryToDelete: categoryId,
      categoryToDeleteName: categoryName,
    });
  }

  confirmDeleteCategory = async () => {
    if (!this.state.categoryToDelete) return;

    const updateURL =
      this.props.baseUrl +
      this.props.apiRouts.CATEGORIES +
      "/" +
      this.state.categoryToDelete;

    this.setState({ 
      delete_category: true,
      showDeleteConfirm: false,
    });

    try {
      const { response } = await API.Delete(updateURL);

      if (response && response.status === 202) {
        await this.getAllCategories(); // Refresh categories list
        this.setState({
          delete_category: false,
          categoryToDelete: null,
          categoryToDeleteName: "",
          new_category_error: false,
          new_category_error_msg: "",
        });
        this.showNotification("Category deleted successfully!", "success");
      }

      if (response && response.status === 400) {
        throw new Error("something went wrong, please try again later");
      }
    } catch (error) {
      this.setState({
        add_category_error: "*" + error,
        delete_category: false,
        categoryToDelete: null,
        categoryToDeleteName: "",
      });
    }
  }

  cancelDeleteCategory = () => {
    this.setState({
      showDeleteConfirm: false,
      categoryToDelete: null,
      categoryToDeleteName: "",
    });
  }

  handleDeleteAccount = () => {
    this.setState({ showDeleteAccountConfirm: true });
  }

  confirmDeleteAccount = async () => {
    this.setState({ showDeleteAccountConfirm: false, deletingAccount: true });
    try {
      const url = this.props.baseUrl + this.props.apiRouts.REGISTER;
      await API.Delete(url);

      localStorage.removeItem("first_name");
      localStorage.removeItem("last_name");
      localStorage.removeItem("email");
      localStorage.removeItem("id");

      this.props.history.push("/home");
      window.location.reload();
    } catch {
      this.setState({ deletingAccount: false });
      this.showNotification("Failed to delete account", "danger");
    }
  }

  cancelDeleteAccount = () => {
    this.setState({ showDeleteAccountConfirm: false });
  }

  async updateCategoryField(categoryId, field, value) {
    const updateURL = this.props.baseUrl + this.props.apiRouts.CATEGORIES + "/" + categoryId;
    
    // Find the current category to preserve the other field
    const currentCategory = this.state.categories.find(cat => (cat.id || cat.value) === categoryId);
    if (!currentCategory) {
      this.showNotification("Category not found", "warning");
      return;
    }
    
    const body = {
      title: currentCategory.title || currentCategory.label, // Preserve current title
      budget: currentCategory.budget !== undefined ? currentCategory.budget : null, // Preserve current budget
    };
    
    // Update only the field being edited
    if (field === "title") {
      body.title = value;
    } else if (field === "budget") {
      body.budget = value === "" || value === null ? null : parseFloat(value);
    }

    try {
      const { response } = await API.Patch(updateURL, body);

      if (response && response.status === 200) {
        await this.getAllCategories(); // Refresh categories list
        this.setState({ editingCategoryId: null, editingField: null });
        this.showNotification("Category updated successfully!", "success");
      }
    } catch (error) {
      this.showNotification("Failed to update category", "warning");
      this.setState({ editingCategoryId: null, editingField: null });
    }
  }

  startEditing = (categoryId, field) => {
    this.setState({ editingCategoryId: categoryId, editingField: field });
  }

  cancelEditing = () => {
    this.setState({ editingCategoryId: null, editingField: null });
  }

  checkCategoryExist() {
    for (let i in this.state.categories) {
      const category = this.state.categories[i];
      const categoryTitle = category.title || category.label;
      if (categoryTitle === this.state.new_category) {
        this.setState({
          new_category_error: true,
          new_category_error_msg: "Category already exists.",
        });
        return true;
      }
      if (this.state.new_category_error) {
        this.setState({
          new_category_error: false,
          new_category_error_msg: "",
        });
      }
    }
    return false;
  }

  formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  getBudgetStatus = (budget, spent) => {
    if (!budget || budget === 0) return null;
    
    const percentage = (spent / budget) * 100;
    
    if (percentage >= 100) {
      return { status: "Over", color: "bg-red-100 text-red-800", percentage: Math.round(percentage) };
    } else if (percentage >= 80) {
      return { status: "Warning", color: "bg-yellow-100 text-yellow-800", percentage: Math.round(percentage) };
    } else {
      return { status: "OK", color: "bg-green-100 text-green-800", percentage: Math.round(percentage) };
    }
  }

  update_remove_enabled = async (remove_enabled) => {
    await this.props.update_remove_enabled(remove_enabled);
    const msg = remove_enabled
      ? "Delete expense enabled"
      : "Delete expense disabled";
    this.showNotification(msg, "info");
  };

  render() {
    if (this.state.loading) {
      return (
        <div className="flex items-center justify-center py-10">
          <Loader />
        </div>
      );
    }

    const personal_update_btn = (
      <span className="flex items-center justify-center gap-2">
        {this.state.personal_update && (
          <svg
            className="h-4 w-4 animate-spin text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        )}
        <span>Update Profile</span>
      </span>
    );

    const add_category_btn = (
      <span className="flex items-center justify-center gap-2">
        {this.state.add_category && (
          <svg
            className="h-4 w-4 animate-spin text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        )}
        <span>Add Category</span>
      </span>
    );


    return (
      <div
        id="setting_container"
        className="mx-auto max-w-2xl px-4 pt-2 pb-6 md:px-6 md:py-6"
      >
        <Notification
          message={this.state.notification.message}
          type={this.state.notification.type}
          show={this.state.notification.show}
          onClose={this.hideNotification}
          duration={3000}
        />

        <ConfirmDialog
          isOpen={this.state.showDeleteConfirm}
          onConfirm={this.confirmDeleteCategory}
          onCancel={this.cancelDeleteCategory}
          title="Delete Category"
          message={`Are you sure you want to delete "${this.state.categoryToDeleteName}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
        />

        <div className="mb-8 hidden md:block">
          <h2 className="text-2xl font-bold text-slate-900">
            Settings
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Manage your profile, categories, and preferences.
          </p>
        </div>

        <div className="space-y-4">
          {/* Personal Details Section */}
          <AccordionSection
            title="Personal Details"
            description="Update your profile information"
            icon={<UserIcon />}
            isOpen={this.state.openSections.personal}
            onToggle={() => this.toggleSection("personal")}
          >
            <div className="space-y-4">
              <FloatingInput
                id="email"
                label="Email"
                type="email"
                value={this.state.user.email}
                onChange={(e) => this.onChange("email", e.target.value)}
                disabled={true}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <FloatingInput
                  id="firstName"
                  label="First Name"
                  value={this.state.user.first_name}
                  onChange={(e) => this.onChange("firstName", e.target.value)}
                />
                <FloatingInput
                  id="lastName"
                  label="Last Name"
                  value={this.state.user.last_name}
                  onChange={(e) => this.onChange("lastName", e.target.value)}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <FloatingPhoneInput
                  id="mobile"
                  label="Mobile"
                  value={this.state.user.mobile}
                  onChange={(value) => this.onChange("mobile", value)}
                  error={this.state.mobileError}
                  errorMessage={this.state.mobileErrorMsg}
                />
              </div>

              {this.state.personal_details_error && (
                <div className="text-sm text-red-600">
                  {this.state.personal_details_error}
                </div>
              )}

              <button
                type="button"
                disabled={this.state.personal_update}
                className="w-full rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
                onClick={() => this.updateUserHandlers()}
              >
                {personal_update_btn}
              </button>
            </div>
          </AccordionSection>

          {/* Categories Section */}
          <AccordionSection
            title="Categories"
            description="Manage categories and budgets"
            icon={<FolderIcon />}
            isOpen={this.state.openSections.categories}
            onToggle={() => this.toggleSection("categories")}
          >
            <div className="space-y-4">
              {/* Add New Category Form */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <h4 className="mb-3 text-sm font-semibold text-slate-900">
                  Add New Category
                </h4>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FloatingInput
                    id="new_category"
                    label="Category Name"
                    value={this.state.new_category}
                    onChange={(e) => this.onChange("new_category", e.target.value)}
                    error={this.state.new_category_error}
                  />
                  <FloatingInput
                    id="new_category_budget"
                    label="Monthly Budget (optional)"
                    type="number"
                    value={this.state.new_category_budget}
                    onChange={(e) => this.onChange("new_category_budget", e.target.value)}
                  />
                </div>

                {this.state.new_category_error_msg && (
                  <div className="mt-2 text-sm text-red-600">
                    {this.state.new_category_error_msg}
                  </div>
                )}

                {this.state.add_category_error && (
                  <div className="mt-2 text-sm text-red-600">
                    {this.state.add_category_error}
                  </div>
                )}

                <button
                  type="button"
                  disabled={this.state.add_category}
                  className="mt-3 w-full rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
                  onClick={() => this.addNewCategoryHandler()}
                >
                  {add_category_btn}
                </button>
              </div>

              {/* Categories Table - scrollable so all categories are visible on mobile + desktop */}
              {this.state.categories.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <div className="overflow-auto max-h-[280px] min-h-[120px] sm:max-h-[340px] md:max-h-[400px]">
                    <table className="w-full">
                      <thead className="sticky top-0 z-10 bg-slate-50 shadow-[0_1px_0_0_rgba(0,0,0,0.05)]">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700">
                            Category
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700">
                            Budget
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-700">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {this.state.categories.map((cat) => {
                          const categoryId = cat.id || cat.value;
                          const categoryTitle = cat.title || cat.label;
                          const budget = cat.budget;
                          const spent = cat.spent || 0;
                          const isOverBudget = cat.isOverBudget || (budget && spent > budget);
                          const isEditingTitle = this.state.editingCategoryId === categoryId && this.state.editingField === "title";
                          const isEditingBudget = this.state.editingCategoryId === categoryId && this.state.editingField === "budget";

                          return (
                            <tr
                              key={categoryId}
                              className={`transition-colors hover:bg-slate-50 ${
                                isOverBudget ? "bg-red-50" : ""
                              }`}
                            >
                              <td className="whitespace-nowrap px-4 py-3">
                                {isEditingTitle ? (
                                  <input
                                    type="text"
                                    defaultValue={categoryTitle}
                                    className="w-full rounded border border-slate-300 px-2 py-1 text-base md:text-sm focus:border-slate-500 focus:outline-none"
                                    onBlur={(e) => {
                                      const newValue = e.target.value.trim();
                                      if (newValue && newValue !== categoryTitle) {
                                        this.updateCategoryField(categoryId, "title", newValue);
                                      } else {
                                        this.cancelEditing();
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.target.blur();
                                      } else if (e.key === "Escape") {
                                        this.cancelEditing();
                                      }
                                    }}
                                    autoFocus
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => this.startEditing(categoryId, "title")}
                                    className="group flex items-center gap-2 text-left text-sm font-medium text-slate-900 hover:text-slate-700 transition-colors"
                                    title="Click to edit category name"
                                  >
                                    <span>{categoryTitle}</span>
                                    <svg
                                      className="h-4 w-4 opacity-40 md:opacity-0 md:group-hover:opacity-50 transition-opacity text-slate-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                      />
                                    </svg>
                                  </button>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3">
                                {isEditingBudget ? (
                                  <input
                                    type="number"
                                    defaultValue={budget || ""}
                                    className="w-full rounded border border-slate-300 px-2 py-1 text-base md:text-sm focus:border-slate-500 focus:outline-none"
                                    placeholder="No budget"
                                    onBlur={(e) => {
                                      const newValue = e.target.value.trim();
                                      if (newValue !== (budget || "").toString()) {
                                        this.updateCategoryField(categoryId, "budget", newValue);
                                      } else {
                                        this.cancelEditing();
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.target.blur();
                                      } else if (e.key === "Escape") {
                                        this.cancelEditing();
                                      }
                                    }}
                                    autoFocus
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => this.startEditing(categoryId, "budget")}
                                    className="group flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
                                    title="Click to edit budget"
                                  >
                                    <span>{budget ? this.formatCurrency(budget) : "-"}</span>
                                    <svg
                                      className="h-4 w-4 opacity-40 md:opacity-0 md:group-hover:opacity-50 transition-opacity text-slate-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                      />
                                    </svg>
                                  </button>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-center">
                                <button
                                  type="button"
                                  disabled={this.state.delete_category}
                                  onClick={() => this.deleteCategoryHandler(categoryId)}
                                  className="rounded p-1 text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                  title="Delete category"
                                >
                                  {this.state.delete_category ? (
                                    <svg
                                      className="h-4 w-4 animate-spin"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      ></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      ></path>
                                    </svg>
                                  ) : (
                                    <img src={remove} alt="Delete" className="h-4 w-4" />
                                  )}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center">
                  <p className="text-sm text-slate-500">No categories yet. Add your first category above.</p>
                </div>
              )}
            </div>
          </AccordionSection>

          {/* General Settings Section */}
          <AccordionSection
            title="General Settings"
            description="Configure app preferences"
            icon={<CogIcon />}
            isOpen={this.state.openSections.general}
            onToggle={() => this.toggleSection("general")}
          >
            <div className="space-y-4">
              <FloatingSelect
                id="currency"
                label="Currency"
                value={this.state.settings.currency}
                onChange={(e) => this.onChange("currency", e.target.value)}
                onBlur={(e) => this.updateSettnigs({ currency: e.target.value })}
                options={this.state.settings.currencies}
              />

              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    Enable Expense Deletion
                  </p>
                  <p className="text-xs text-slate-500">
                    Allow deleting expenses from the budget view
                  </p>
                </div>
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={this.props.user.remove_enabled}
                    onChange={(e) =>
                      this.update_remove_enabled(e.target.checked)
                    }
                    className="peer sr-only"
                  />
                  <div className="peer h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-slate-900 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none"></div>
                </label>
              </div>
            </div>
          </AccordionSection>

          {/* Danger Zone */}
          <div className="mt-8">
            <div className="mb-3 flex items-center gap-3">
              <div className="h-px flex-1 bg-red-200" />
              <span className="text-xs font-semibold uppercase tracking-widest text-red-400">
                Danger Zone
              </span>
              <div className="h-px flex-1 bg-red-200" />
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                {/* Icon + text row on mobile */}
                <div className="flex items-start gap-3 sm:contents">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                    <svg
                      className="h-5 w-5 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-red-700">Delete Account</p>
                    <p className="mt-1 text-xs text-red-500">
                      Permanently deletes your account, all expenses, categories, and settings. This cannot be undone.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={this.state.deletingAccount}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:flex-shrink-0 sm:justify-start"
                  onClick={this.handleDeleteAccount}
                >
                  {this.state.deletingAccount ? (
                    <>
                      <svg
                        className="h-4 w-4 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                        />
                      </svg>
                      Delete Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        <ConfirmDialog
          isOpen={this.state.showDeleteAccountConfirm}
          onConfirm={this.confirmDeleteAccount}
          onCancel={this.cancelDeleteAccount}
          title="Delete Account"
          message="This will permanently delete your account and all your data. This action cannot be undone."
          confirmText="Delete My Account"
          cancelText="Cancel"
          variant="danger"
        />
      </div>
    );
  }
}

export default withRouter(Settings);
